// ============================================
// SUGERENCIASBUSQUEDA.JS — el desplegable que ayuda mientras se escribe
// ============================================
// BUSCADOR-PRO (2026-09-21). Ofrece marcas, categorias y modelos AGRUPADOS, para que quien busca
// no tenga que acertar la palabra exacta. Tocar una sugerencia busca esa cosa concreta.
//
// Decisiones que no son adorno:
//   - Se pide a partir de 2 letras y con 180 ms de espera desde la ultima tecla: sin eso, escribir
//     "camara" dispara seis consultas y solo importa la ultima.
//   - Se cierra al tocar fuera, con Escape, y al elegir. En celular ademas ocupa todo el ancho.
//   - Si la peticion falla, NO se enseña nada: un autocompletado roto no puede estorbar al
//     buscador, que funciona igual sin el.

import React, { useEffect, useRef, useState } from 'react';
import { API_BASE } from '../config/api';
import { authHeaders } from '../utils/storage';

const PP = "'Poppins', sans-serif";

const ETIQUETAS = { marcas: 'Marcas', categorias: 'Categorías', modelos: 'Modelos' };

export default function SugerenciasBusqueda({ texto, visible, onElegir, onCerrar, esCelular }) {
  const [datos, setDatos] = useState(null);
  const temporizador = useRef(null);
  const peticion = useRef(0);

  useEffect(() => {
    const t = String(texto || '').trim();
    if (!visible || t.length < 2) { setDatos(null); return undefined; }
    clearTimeout(temporizador.current);
    temporizador.current = setTimeout(async () => {
      const mio = ++peticion.current;
      try {
        const r = await fetch(`${API_BASE}/api/buscador/sugerencias?q=${encodeURIComponent(t)}`,
          { headers: authHeaders() }).then(x => x.json());
        // Si mientras llegaba esta se disparo otra mas nueva, esta ya no vale.
        if (mio !== peticion.current) return;
        const hay = (r.marcas || []).length + (r.categorias || []).length + (r.modelos || []).length;
        setDatos(hay ? r : null);
      } catch (e) { setDatos(null); }
    }, 180);
    return () => clearTimeout(temporizador.current);
  }, [texto, visible]);

  useEffect(() => {
    if (!visible) return undefined;
    const alTeclear = (e) => { if (e.key === 'Escape') onCerrar && onCerrar(); };
    window.addEventListener('keydown', alTeclear);
    return () => window.removeEventListener('keydown', alTeclear);
  }, [visible, onCerrar]);

  if (!visible || !datos) return null;

  const grupos = ['marcas', 'categorias', 'modelos']
    .map(k => [k, datos[k] || []])
    .filter(([, arr]) => arr.length);
  if (!grupos.length) return null;

  return (
    <div
      role="listbox"
      style={{
        position: 'absolute', top: 'calc(100% + 6px)', left: 0, right: 0, zIndex: 60,
        background: '#fff', borderRadius: 12, border: '1px solid #e6e8ea',
        boxShadow: '0 12px 32px rgba(0,0,0,0.14)', overflow: 'hidden',
        fontFamily: PP, maxHeight: esCelular ? 320 : 420, overflowY: 'auto',
      }}
      onMouseDown={(e) => e.preventDefault()}   // que el input no pierda el foco al tocar
    >
      {grupos.map(([clave, arr]) => (
        <div key={clave}>
          <div style={{
            padding: '8px 14px 4px', fontSize: 10, fontWeight: 700, letterSpacing: 0.6,
            textTransform: 'uppercase', color: '#9aa3ab', background: '#fafbfc',
          }}>{ETIQUETAS[clave]}</div>
          {arr.map((it, i) => (
            <button
              key={clave + i}
              onClick={() => onElegir && onElegir(it.texto, clave, it)}
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10,
                width: '100%', textAlign: 'left', background: 'transparent', border: 'none',
                padding: '10px 14px', minHeight: 40, cursor: 'pointer',
                fontFamily: PP, fontSize: 13, color: '#2e3338',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = '#f4f7fa'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
            >
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {it.texto}
                {clave === 'modelos' && it.marca && (
                  <span style={{ color: '#9aa3ab', fontSize: 11 }}> · {it.marca}</span>
                )}
              </span>
              <span style={{ flexShrink: 0, fontSize: 11, color: '#9aa3ab' }}>
                {clave === 'marcas' && it.productos ? `${it.productos} productos` : null}
                {clave === 'modelos' ? (it.stock > 0 ? `${it.stock} en stock` : 'sin stock') : null}
              </span>
            </button>
          ))}
        </div>
      ))}
    </div>
  );
}
